import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UseQueryResult, useQuery } from 'react-query';
import { differenceInHours, add } from 'date-fns';
import { isUndefined } from 'lodash';
import { ScheduleAvailableTimes } from 'domains/calendars/types';
import { getScheduleAvailableTimes } from 'domains/calendars/queries';
import { getSchedule } from './queries';
import { Schedule } from './types';

type ScheduleStateProviderContextValue = {
  selectedDayIndex?: number;
  selectedTime?: Date;
  calendarId?: number;
  therapistName?: string;
  clientId?: string;
  scheduleId?: string;
  scheduleQuery: UseQueryResult<Schedule, unknown>;
  availableTimes: ScheduleAvailableTimes[];
  setAvailableTimes: React.Dispatch<React.SetStateAction<ScheduleAvailableTimes[]>>;
  scheduleInformation?: Schedule;
  setSelectedDayIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedTime: React.Dispatch<React.SetStateAction<Date | undefined>>;
  setCalendarId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setTherapistName: React.Dispatch<React.SetStateAction<string | undefined>>;
  isLoadingSelectedDay: boolean;
};

const ScheduleStateContext = createContext<ScheduleStateProviderContextValue | undefined>(undefined);

export const SCHEDULE_MAX_TIME_GAP = 2;
export const isTimeSlotAvailable = (timeSlot: Date): boolean => {
  return differenceInHours(timeSlot, new Date()) >= SCHEDULE_MAX_TIME_GAP;
};

/** Generates 7 placeholder days, starting 3 days after today */
const getAvailableTimesInitialState = (): ScheduleAvailableTimes[] => {
  const generatedDates: ScheduleAvailableTimes[] = [];

  for (let i = 3; i <= 10; i++) {
    const now = new Date(`${new Date().toISOString().split('T')[0]}T12:00:00.000Z`);
    generatedDates.push({ day: add(now, { days: i }), availableTimes: [] });
  }

  return generatedDates;
};

export const ScheduleStateProvider: React.FC = ({ children }) => {
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | undefined>();
  const [selectedTime, setSelectedTime] = useState<Date | undefined>();
  const [calendarId, setCalendarId] = useState<number | undefined>();
  const [therapistName, setTherapistName] = useState<string | undefined>();
  const [availableTimes, setAvailableTimes] = useState<ScheduleAvailableTimes[]>(getAvailableTimesInitialState());
  const [isLoadingSelectedDay, setIsLoadingSelectedDay] = useState<boolean>(false);

  const { clientId, scheduleId } = useParams<{ clientId: string; scheduleId: string }>();

  if (isUndefined(clientId) || isUndefined(scheduleId)) {
    throw new Error(`Bad schedule URL missing clientId (${clientId}) OR scheduleId (${scheduleId})`);
  }

  const scheduleQuery = useQuery(['schedule', clientId, scheduleId], () => getSchedule(clientId, scheduleId));

  useEffect(() => {
    if (isUndefined(selectedDayIndex)) return;
    const selectedDate = availableTimes[selectedDayIndex].day;
    const fetchSchedule = async () => {
      setIsLoadingSelectedDay(true);
      const result = await getScheduleAvailableTimes(selectedDate, clientId, scheduleId);
      availableTimes[selectedDayIndex].availableTimes = result.availableTimes;
      setIsLoadingSelectedDay(false);
    };
    fetchSchedule();
  }, [selectedDayIndex]);

  const ctxValue: ScheduleStateProviderContextValue = {
    selectedDayIndex,
    setSelectedDayIndex,
    selectedTime,
    setSelectedTime,
    calendarId,
    setCalendarId,
    therapistName,
    setTherapistName,
    clientId,
    scheduleId,
    scheduleQuery,
    availableTimes,
    setAvailableTimes,
    scheduleInformation: scheduleQuery.data,
    isLoadingSelectedDay,
  };

  return <ScheduleStateContext.Provider value={ctxValue}>{children}</ScheduleStateContext.Provider>;
};

export const useScheduleState = () => {
  const ctx = useContext(ScheduleStateContext);

  if (!ctx) {
    throw new Error(`You must wrap your component to a schedule state context!`);
  }

  return ctx;
};
